import Check from '@mui/icons-material/Check';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { Brand } from '@src/Branding';
import { BaseModal } from '@src/common/components/BaseModal';
import { Button } from '@src/common/components/button/Button';
import { Divider } from '@src/common/components/Divider';
import { CloseConfirmModalFooter } from '@src/common/components/modal/CloseConfirmModalFooter';
import { Stack } from '@src/common/components/Stack';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { Typography } from '@src/common/components/Typography';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import Config from '@src/config/Config';
import { CloseEditOrderButton } from '@src/orders/components/CloseEditOrderButton';
import { ClientOrderSubmissionState } from '@src/orders/domain/ClientOrderSubmissionState';
import type { Order } from '@src/orders/domain/Order';
import { OrderStatus } from '@src/orders/domain/Order';
import { useNavigateToOrderDetails } from '@src/orders/hooks/useNavigateToOrderDetails';
import { useRefreshOrder } from '@src/orders/hooks/useRefreshOrder';
import { useUpdateOrder } from '@src/orders/hooks/useUpdateOrder';
import { useUpdateOrderAsDraft } from '@src/orders/hooks/useUpdateOrderAsDraft';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export const ClientUserEditOrderFooter = ({
  order,
  readFormValues,
  formIsDirty,
  onSubmit,
  triggerValidation,
}: {
  order: Order;
  onSubmit: () => void;
  triggerValidation: () => Promise<boolean>;
  formIsDirty: boolean;
  readFormValues: () => Record<string, unknown>;
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { refreshOrder } = useRefreshOrder({ id: order.id });
  const { navigateToOrderDetails } = useNavigateToOrderDetails();
  const fields = order.getFields().fields;

  const { updateOrderAsDraft, isPending: isPendingUpdateOrderAsDraft } =
    useUpdateOrderAsDraft({
      orderId: order.id,
      onSuccess: () => {
        refreshOrder();
        showToast({
          severity: ToastLevel.success,
          message: t('editOrder.success'),
        });
        void navigateToOrderDetails({ orderId: order.id });
      },
      onError: () =>
        showToast({
          severity: ToastLevel.error,
          message: t('editOrder.error'),
        }),
    });

  const saveProgress = () => {
    updateOrderAsDraft({ data: readFormValues(), fields });
  };

  const { updateOrder, isPending: isPendingUpdateOrder } = useUpdateOrder({
    orderId: order.id,
    onSuccess: () => {
      refreshOrder();
      void navigateToOrderDetails({ orderId: order.id });
    },
    onError: () =>
      showToast({
        severity: ToastLevel.error,
        message: t('editOrder.error'),
      }),
  });

  const isDraft = order.status === OrderStatus.DRAFT;
  const onSave = () => {
    if (isDraft) {
      saveProgress();
    } else {
      updateOrder({
        data: readFormValues(),
        fields,
        clientSubmissionState: ClientOrderSubmissionState.COMPLETED,
      });
    }
  };

  const [open, setOpen] = useState(false);
  const [isOrderPartial, setIsOrderPartial] = useState(true);
  const isNova = Config.VITE_BRAND === Brand.Nova;
  const closeModal = () => {
    setOpen(false);
    setIsOrderPartial(false);
  };

  const openModal = ({ orderIsPartial }: { orderIsPartial: boolean }) => {
    setIsOrderPartial(orderIsPartial);
    setOpen(true);
  };

  const submitPartialOrder = () => {
    updateOrder({
      data: readFormValues(),
      fields,
      clientSubmissionState: ClientOrderSubmissionState.PARTIAL,
    });
  };

  const disabled = isPendingUpdateOrder || isPendingUpdateOrderAsDraft;
  return (
    <>
      <BaseModal
        open={open}
        onClose={closeModal}
        title={
          isOrderPartial
            ? t('editOrder.clientUser.partialSubmit.title')
            : t('editOrder.clientUser.submit.title')
        }
      >
        <Typography
          variant="body1"
          style={{ marginBottom: theme.spacing['6'] }}
        >
          <Trans
            i18nKey={
              isOrderPartial
                ? 'editOrder.clientUser.partialSubmit.description'
                : 'editOrder.clientUser.submit.description'
            }
            values={{ brandEmail: Config.VITE_BRAND_EMAIL }}
            components={{
              strong: <strong style={{ fontWeight: 700 }} />,
              mail: <a href="mailto:permits@novapermits.comm" />,
            }}
          />
        </Typography>
        <Divider style={{ marginBottom: theme.spacing['6'] }} />
        <CloseConfirmModalFooter
          onClose={closeModal}
          isSubmitType
          confirm={
            isOrderPartial
              ? () => {
                  submitPartialOrder();
                  closeModal();
                }
              : () => {
                  onSubmit();
                  closeModal();
                }
          }
          closeLabel={t('editOrder.clientUser.submit.cancel')}
          confirmLabel={
            isOrderPartial
              ? t('editOrder.clientUser.partialSubmit.confirm')
              : t('editOrder.clientUser.submit.confirm')
          }
        />
      </BaseModal>
      <Stack
        zIndex={10}
        position="sticky"
        bottom="0"
        style={{ backgroundColor: theme.palette.surface.surface }}
        paddingBottom={theme.spacing['6']}
        marginX="40px"
      >
        <Divider style={{ marginBottom: theme.spacing['6'] }} />
        <Stack direction="row" justifyContent="space-between">
          <CloseEditOrderButton
            orderId={order.id}
            formIsDirty={formIsDirty}
            onSave={onSave}
          />
          <Stack direction="row" gap={theme.spacing['2']}>
            {isDraft && (
              <Button
                type="button"
                disabled={disabled}
                onClick={saveProgress}
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
              >
                {t('saveProgress')}
              </Button>
            )}
            <Button
              type="button"
              disabled={disabled}
              startIcon={<Check />}
              variant="contained"
              onClick={() => {
                void triggerValidation().then((isValid) => {
                  if (isValid) {
                    openModal({ orderIsPartial: false });
                  } else {
                    //This is to change the mode of the form so validations
                    // are triggered after changes are brought to the form.
                    // This allow user to see the fix as they correct
                    // their mistakes instead of having to click on submit again
                    onSubmit();
                    if (isNova) {
                      openModal({ orderIsPartial: true });
                    }
                  }
                });
              }}
            >
              {t('submit')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};
